import NextLink from 'next/link';
import styled, { css } from 'styled-components';
import { rem } from '~/src/helpers/rem-converter.helper';
import { mediaQuery } from '~/src/styles/mixins';
import {
    MAX_1919,
    MAX_1679,
    MAX_1439,
    MAX_1279,
    MAX_767,
    MAX_567,
    MAX_479,
    MAX_319,
} from '~/src/styles/tokens/break-points';
import { FONTS } from '~/src/styles/style-helpers/font-helper';
import { COLORS } from '~/src/styles/tokens/colors';
import { Z_INDEX } from '~/src/styles/tokens/z-index';
import { BORDER_RADIUS } from '~/src/styles/tokens/border-radius';

const headerLogoCommonStyles = `
    display: flex;
    align-items: center;
    color: ${COLORS.textLightPrimary};
    text-decoration: inherit;
`;

export const ReferralProductHeaderContainer = styled.header<{ isShowGeolocationBlock: boolean }>`
    position: fixed;
    top: 0;
    z-index: ${Z_INDEX.top};
    height: ${rem(52)};
    width: 100%;
    background: ${COLORS.backgroundModal};
    box-shadow: ${(p) =>
        p.isShowGeolocationBlock
            ? 'none'
            : `0 4px ${rem(16)} 0 rgba(0, 0, 0, 0.08), 0 0 ${rem(6)} 0 rgba(0, 0, 0, 0.08)`};
    padding: ${rem(10)} ${rem(184)};

    ${mediaQuery(
        { maxWidth: MAX_1919 },
        css`
            padding: ${rem(10)} ${rem(160)};
        `,
    )};

    ${mediaQuery(
        { maxWidth: MAX_1679 },
        css`
            padding: ${rem(10)} ${rem(88)};
        `,
    )};

    ${mediaQuery(
        { maxWidth: MAX_1439 },
        css`
            padding: ${rem(10)} ${rem(56)};
        `,
    )};

    ${mediaQuery(
        { maxWidth: MAX_1279 },
        css`
            padding: ${rem(10)} ${rem(40)};
        `,
    )};

    ${mediaQuery(
        { maxWidth: MAX_767 },
        css`
            padding: ${rem(10)} ${rem(20)};
        `,
    )};
`;

export const ReferralProductHeader = styled.div`
    max-width: ${rem(1552)};
    margin: 0 auto;
    display: flex;
    justify-content: space-between;
    align-items: center;
    gap: ${rem(44)};
`;

export const ReferralProductHeaderLogoWrapper = styled.div`
    display: flex;
`;

export const HeaderLogoLink = styled(NextLink)`
    ${headerLogoCommonStyles};
`;
export const HeaderLogoContainer = styled.div`
    ${headerLogoCommonStyles};
`;

export const ReferralProductHeaderText = styled.span`
    ${FONTS.wide.medium.size17};
    line-height: ${rem(17)};
    display: inline-flex;
    margin-left: ${rem(8)};
    align-items: center;
    color: ${COLORS.textLightPrimary};
    text-decoration: inherit;
`;

export const ReferralProductHeaderGeoIconWrapper = styled.div`
    width: ${rem(16)};
    height: ${rem(16)};
    display: flex;
    align-items: center;
`;

export const ReferralProductHeaderGeoWrapper = styled.div`
    display: flex;
    align-items: center;
    cursor: pointer;
`;

export const ReferralProductHeaderGeoText = styled.a<{ withBurger: boolean; }>`
    ${FONTS.compact.medium.size14};
    line-height: ${rem(24)};
    color: ${COLORS.textLightSecondary};
    display: inline-flex;
    margin-left: ${rem(4)};
    white-space: nowrap;
    
    ${p => p.withBurger && css`
        max-width: ${rem(355)};
        overflow: hidden;
        text-overflow: ellipsis;
        display: inline-block;
    `};

    ${p => mediaQuery(
        { maxWidth: MAX_767 },
        css`
            max-width: ${p.withBurger ? rem(305) : rem(400)};
            overflow: hidden;
            text-overflow: ellipsis;
            display: inline-block;
        `,
    )};

    ${p => mediaQuery(
        { maxWidth: MAX_567 },
        css`
            max-width: ${p.withBurger ? rem(220) : rem(270)};
        `,
    )};

    ${p => mediaQuery(
        { maxWidth: MAX_479 },
        css`
            max-width: ${p.withBurger ? rem(97) : rem(145)};
        `,
    )};

    ${p => mediaQuery(
        { maxWidth: MAX_319 },
        css`
            max-width: ${p.withBurger ? rem(97) :rem(70)};
        `,
    )};
`;

export const NavigationBlock = styled.nav<{ mobileMenuIsOpen: boolean }>`
    width: 100%;
    height: ${rem(52)};
    margin-top: ${rem(-10)};
    margin-bottom: ${rem(-10)};
    display: flex;
    align-items: center;
    justify-content: flex-start;
    transition: opacity 0.3s;

    ${(p) =>
        !p.mobileMenuIsOpen &&
        mediaQuery(
            { maxWidth: MAX_767 },
            css`
                opacity: 0;
                visibility: hidden;
                position: fixed;
                top: ${rem(60)};
                left: 0;
                right: 0;
                bottom: 0;
                background: ${COLORS.backgroundPrimary};
                width: 100vw;
                height: 100vh;
                display: flex;
                align-items: flex-start;
                z-index: ${Z_INDEX.top};
            `,
        )};

    ${(p) =>
        p.mobileMenuIsOpen &&
        mediaQuery(
            { maxWidth: MAX_767 },
            css`
                opacity: 1;
                visibility: visible;
                position: fixed;
                top: ${rem(60)};
                left: 0;
                right: 0;
                bottom: 0;
                background: ${COLORS.backgroundPrimary};
                width: 100vw;
                height: 100vh;
                display: flex;
                align-items: flex-start;
                z-index: ${Z_INDEX.top};
            `,
        )}
`;

export const NavItemList = styled.ul`
    height: 100%;
    list-style: none;

    ${mediaQuery(
        {
            maxWidth: MAX_767,
        },
        css`
            margin-top: ${rem(10)};
            width: 100%;
        `,
    )}
`;

export const NavItemLink = styled(NextLink)`
    display: flex;
    align-items: center;
    height: 100%;
    color: inherit;
    text-decoration: none;
    position: relative;

    &:before {
        content: '';
        width: 0;
        height: 2px;
        border-radius: ${BORDER_RADIUS.onePx};
        background-color: rgba(188, 195, 208, 0.5);
        transform: scale(0, 1);
        transform-origin: 0 center;
        transition: transform 0.3s cubic-bezier(0.64, 0.09, 0.08, 1) 0s;
        position: absolute;
        bottom: 0;
    }
`;

export const NavItem = styled.li<{ itemIsTouched: boolean; x: number; width: number }>`
    height: 100%;
    ${FONTS.sans.medium.size17};
    color: ${COLORS.textLightPrimary};
    padding: 0 ${rem(16)};
    position: relative;
    overflow: hidden;

    &:hover {
        ${NavItemLink}:before {
            width: 100%;
            transform: scale(1);
        }
    }

    ${(p) =>
        mediaQuery(
            {
                maxWidth: MAX_767,
            },
            css`
                padding: 0 ${rem(20)};
                max-height: ${rem(44)};

                &:hover {
                    ${NavItemLink}:before {
                        display: none;
                    }
                }

                :before {
                    content: '';
                    position: absolute;
                    left: ${p.x}px;
                    top: 50%;
                    transform: translate(-50%, -50%);
                    width: ${p.itemIsTouched ? `${rem(p.width * 2.2)}` : 0};
                    height: 100%;
                    margin: 0 ${rem(-20)};
                    opacity: ${p.itemIsTouched ? 1 : 0};
                    background-color: ${COLORS.constantGreyscale200};
                    transition: width 0.3s;
                }
            `,
        )}
`;

export const MobileMenuContainer = styled.div`
    display: none;

    ${mediaQuery(
        { maxWidth: MAX_767 },
        css`
            display: flex;
            align-items: center;
        `,
    )};
`;

export const MobileMenuWithGeolocation = styled.div`
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: ${rem(12)};
`;
